import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

export default function AdminLoginPage() {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
    });
    
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isLogin) {
                await login(formData.email, formData.password);
                toast.success('Welcome back!');
            } else {
                await register(formData.email, formData.password, formData.name);
                toast.success('Account created successfully!');
            }
            navigate('/admin');
        } catch (error) {
            console.error('Auth error:', error);
            toast.error(error.response?.data?.detail || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <Link 
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to website
                </Link>

                <div className="p-8 rounded-3xl border border-border bg-background shadow-xl">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold mb-2">
                            {isLogin ? 'Admin Login' : 'Create Admin Account'}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {isLogin 
                                ? 'Sign in to manage your Aitrixy dashboard' 
                                : 'Set up your admin account'
                            }
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!isLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Your name"
                                    required={!isLogin}
                                    data-testid="admin-name-input"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="admin@aitrixy.com"
                                required
                                data-testid="admin-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                required
                                data-testid="admin-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full rounded-full"
                            disabled={loading}
                            data-testid="admin-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {isLogin ? 'Signing in...' : 'Creating account...'}
                                </>
                            ) : (
                                isLogin ? 'Sign In' : 'Create Account'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <button
                            onClick={() => setIsLogin(!isLogin)}
                            className="text-sm text-primary hover:underline"
                            data-testid="admin-toggle-mode"
                        >
                            {isLogin 
                                ? "Don't have an account? Create one" 
                                : 'Already have an account? Sign in'
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
